import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import type { OptionType } from '@/api/common';
import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import { FieldLabel } from '@/components/Form/fields';
import ChannelMultiSelect from '@/features/inputs/ChannelMultiSelect';
import VendorMultiSelect from '@/features/inputs/VendorMultiSelect';

type Props = {
  open: boolean;
  channels: string[];
  vendors: string[];
  onFiltersChange: (channels: string[], vendors: string[]) => void;
  onClose: () => void;
};

const RuleFilterDialog = ({ open, channels, vendors, onFiltersChange, onClose }: Props) => {
  const [selectedChannels, setSelectedChannels] = useState<string[]>(channels);
  const [selectedVendors, setSelectedVendors] = useState<string[]>(vendors);

  const handleFiltersReset = () => {
    setSelectedChannels([]);
    setSelectedVendors([]);
  };

  const handleClose = () => {
    setSelectedChannels(channels);
    setSelectedVendors(vendors);
    onClose();
  };

  const handleChannelChange = (channels: OptionType[] | null) => {
    setSelectedChannels((channels ?? []).map((channel) => channel.id));
  };

  const handleVendorChange = (vendors: OptionType[] | null) => {
    setSelectedVendors((vendors ?? []).map((vendor) => vendor.id));
  };

  const handleApplyFilters = () => {
    onFiltersChange(selectedChannels, selectedVendors);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Filter</Trans>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-6">
          <div className="flex justify-between">
            <Button onClick={handleFiltersReset}>
              <Trans>Clear All Filters</Trans>
            </Button>
          </div>
          <div className="flex flex-col gap-1">
            <FieldLabel>
              <Trans>Channels</Trans>
            </FieldLabel>
            <ChannelMultiSelect
              value={selectedChannels.map((channel) => ({ id: channel }))}
              onChange={handleChannelChange}
              size="large"
            />
          </div>
          <div className="flex flex-col gap-1">
            <FieldLabel>
              <Trans>Vendors</Trans>
            </FieldLabel>
            <VendorMultiSelect
              value={selectedVendors.map((vendor) => ({ id: vendor }))}
              onChange={handleVendorChange}
              size="large"
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose
            render={
              <Button size="large">
                <Trans>Cancel</Trans>
              </Button>
            }
          />
          <Button size="large" variant="on-primary" onClick={handleApplyFilters}>
            <Trans>Apply Filter</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RuleFilterDialog;
